import { useState } from 'react'
import { Copy, MoreVertical, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { useAuth, useLoadSkillForCopy } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import type { Skill } from '@/types'
import { DeleteSkillDialog } from './DeleteSkillDialog'
import { CreateSkillDialog } from './CreateSkillDialog'

interface SkillActionsMenuProps {
  projectId: string
  skill: Skill
}

export function SkillActionsMenu({ projectId, skill }: SkillActionsMenuProps) {
  const { hasPermission } = useAuth(projectId)
  const [deleting, setDeleting] = useState(false)
  const copySource = useLoadSkillForCopy(projectId, skill.id)

  const canDelete = !skill.isReadOnly && hasPermission(Permissions.Project.Skill.Delete, projectId)
  const canCopy = skill.isSystem && hasPermission(Permissions.Project.Skill.Create, projectId)

  if (!canDelete && !canCopy) {
    return null
  }

  const startCopy = () =>
    copySource.mutate(undefined, {
      onError: error => toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to load the skill to copy.'),
    })

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            aria-label={`Actions for ${skill.displayName}`}
            data-testid={`skill-actions-${skill.name}`}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canCopy && (
            <DropdownMenuItem
              data-testid={`skill-copy-${skill.name}`}
              onSelect={startCopy}
              disabled={copySource.isPending}
            >
              <Copy className="h-4 w-4" />
              {copySource.isPending ? 'Loading…' : 'Copy to project'}
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setDeleting(true)}>
              <Trash2 className="h-4 w-4" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {canDelete && <DeleteSkillDialog projectId={projectId} skill={skill} open={deleting} onOpenChange={setDeleting} />}
      {canCopy && copySource.data && (
        <CreateSkillDialog
          projectId={projectId}
          open
          onOpenChange={open => { if (!open) copySource.reset() }}
          title={`Copy “${skill.displayName}” to project`}
          initialValues={{
            displayName: `${skill.displayName} (copy)`,
            description: skill.description ?? '',
            content: copySource.data.content,
          }}
        />
      )}
    </>
  )
}
